import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Typography,
  Button,
  alpha,
  Stack,
} from "@mui/material";
import {
  Inventory2 as ProductIcon,
  Schedule as ValidityIcon,
  Autorenew as RenewIcon,
  Refresh as RefreshIcon,
  ReceiptLong as BillIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

const formatMoney = (value, currency = "INR") => {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  const prefix = currency === "INR" || !currency ? "₹" : `${currency} `;
  return `${prefix}${n.toLocaleString("en-IN")}`;
};

const MetaPill = ({ icon, label, value, tone = "primary" }) => {
  const accent = tone === "energy" ? crmColors.energy : crmColors.primary;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.15,
        py: 0.55,
        borderRadius: "10px",
        bgcolor: alpha(accent.main, tone === "energy" ? 0.09 : 0.07),
        border: `1px solid ${alpha(accent.main, tone === "energy" ? 0.18 : 0.14)}`,
        minWidth: 108,
      }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "7px",
          display: "grid",
          placeItems: "center",
          bgcolor: alpha(accent.main, 0.14),
          color: tone === "energy" ? crmColors.energy.dark : crmColors.primary.main,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: crmColors.text.secondary,
            lineHeight: 1.1,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 700,
            color: crmColors.text.primary,
            lineHeight: 1.25,
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const ProductList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const result = await apiService.getProductRateList();
    if (result.success) {
      setRows(Array.isArray(result.data) ? result.data : []);
    } else {
      toast.error(result.error || "Failed to load products");
      setRows([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const productCount = useMemo(() => {
    const codes = new Set(rows.map((r) => r.product_code).filter(Boolean));
    return codes.size;
  }, [rows]);

  const openBill = (productCode = "") => {
    const q = productCode ? `?product=${encodeURIComponent(productCode)}` : "";
    navigate(`/billdesk${q}`);
  };

  const actionBtnSx = {
    textTransform: "none",
    borderRadius: "8px",
    fontWeight: 600,
    borderColor: alpha("#fff", 0.45),
    color: "#fff",
    "&:hover": { borderColor: "#fff", bgcolor: alpha("#fff", 0.1) },
  };

  return (
    <PageContentLayout
      title="Product List"
      subtitle="Catalog rates and billing cycles"
      barSubtitle={`${productCount} product${productCount === 1 ? "" : "s"}`}
      breadcrumbs={[
        { label: "Product List", icon: <ProductIcon sx={{ color: "#ffffff", fontSize: 18 }} /> },
      ]}
      backTo="/dashboard"
      darkBar
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchProducts}
            disabled={loading}
            sx={actionBtnSx}
          >
            Refresh
          </Button>
        </Stack>
      }
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: crmColors.primary.main }} />
        </Box>
      ) : rows.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 7,
            px: 2,
            borderRadius: 2.5,
            border: `1px dashed ${crmColors.border.default}`,
            bgcolor: alpha(crmColors.primary.main, 0.02),
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              mx: "auto",
              mb: 1.25,
              borderRadius: "14px",
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(crmColors.primary.main, 0.1),
              color: crmColors.primary.main,
            }}
          >
            <ProductIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: crmColors.text.primary }}>
            No products found
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add products in the catalog to create bills
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.15, width: "100%" }}>
          {rows.map((row, idx) => {
            const isPaid = (row.rate_type || "").toLowerCase() === "paid";
            return (
              <Card
                key={`${row.product_code || "p"}-${row.rate_id || idx}`}
                elevation={0}
                sx={{
                  width: "100%",
                  borderRadius: "14px",
                  overflow: "hidden",
                  border: `1px solid ${crmColors.border.default}`,
                  background: `linear-gradient(120deg, ${crmColors.background.paper} 0%, ${alpha(crmColors.primary.main, 0.035)} 100%)`,
                  transition: "transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    borderColor: alpha(crmColors.primary.main, 0.32),
                    boxShadow: `0 8px 22px ${alpha(crmColors.primary.main, 0.1)}`,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: { xs: 1.25, md: 2 },
                    width: "100%",
                    pl: 0,
                    pr: 1.5,
                    py: 0,
                    minHeight: 64,
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 4, sm: 5 },
                      alignSelf: "stretch",
                      minHeight: 64,
                      background: `linear-gradient(180deg, ${crmColors.primary.darker} 0%, ${crmColors.hero.mid} 55%, ${crmColors.logo.light} 100%)`,
                      flexShrink: 0,
                    }}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.15,
                      flex: "1 1 220px",
                      minWidth: 0,
                      py: 1.1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "11px",
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        background: `linear-gradient(145deg, ${crmColors.primary.main}, ${crmColors.hero.start})`,
                        color: "#fff",
                        boxShadow: `0 4px 10px ${alpha(crmColors.primary.main, 0.28)}`,
                      }}
                    >
                      <ProductIcon sx={{ fontSize: 18 }} />
                    </Box>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.65, flexWrap: "wrap" }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 14,
                            color: crmColors.text.primary,
                            lineHeight: 1.25,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: { xs: "100%", sm: 300 },
                          }}
                        >
                          {row.product_name || "—"}
                        </Typography>
                        <Chip
                          label={row.product_code || "—"}
                          size="small"
                          sx={{
                            height: 20,
                            fontWeight: 700,
                            fontSize: 10.5,
                            bgcolor: alpha(crmColors.primary.main, 0.1),
                            color: crmColors.primary.main,
                            "& .MuiChip-label": { px: 0.85 },
                          }}
                        />
                        <Chip
                          label={row.rate_type || "—"}
                          size="small"
                          sx={{
                            height: 20,
                            fontWeight: 700,
                            fontSize: 10.5,
                            bgcolor: isPaid
                              ? alpha(crmColors.success.main, 0.12)
                              : alpha(crmColors.text.secondary, 0.1),
                            color: isPaid ? crmColors.success.dark : crmColors.text.secondary,
                            "& .MuiChip-label": { px: 0.85 },
                          }}
                        />
                      </Box>
                      {row.product_description ? (
                        <Typography
                          sx={{
                            mt: 0.2,
                            fontSize: 11.5,
                            color: crmColors.text.secondary,
                            lineHeight: 1.3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.product_description}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                      ml: { xs: 1.5, sm: "auto" },
                      mr: { xs: 0.25, sm: 0 },
                      pb: { xs: 1.15, sm: 0 },
                      py: { sm: 1.1 },
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.35,
                        py: 0.55,
                        borderRadius: "10px",
                        background: `linear-gradient(135deg, ${alpha(crmColors.primary.main, 0.1)} 0%, ${alpha(crmColors.logo.light, 0.12)} 100%)`,
                        border: `1px solid ${alpha(crmColors.primary.main, 0.16)}`,
                        minWidth: 104,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 9.5,
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: crmColors.text.secondary,
                          lineHeight: 1.1,
                        }}
                      >
                        Product rate
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 17,
                          fontWeight: 800,
                          color: crmColors.primary.main,
                          letterSpacing: "-0.02em",
                          lineHeight: 1.2,
                        }}
                      >
                        {formatMoney(row.product_rate, row.currency)}
                      </Typography>
                    </Box>

                    <MetaPill
                      icon={<RenewIcon sx={{ fontSize: 14 }} />}
                      label="Renewal"
                      value={formatMoney(row.renewal_rate, row.currency)}
                    />
                    <MetaPill
                      icon={<ValidityIcon sx={{ fontSize: 14 }} />}
                      label="Cycle"
                      value={
                        row.billing_cycle_months
                          ? `${row.billing_cycle_months} mo`
                          : row.validity || "—"
                      }
                      tone="energy"
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<BillIcon sx={{ fontSize: 16 }} />}
                      onClick={() => openBill(row.product_code)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "8px",
                        borderColor: alpha(crmColors.primary.main, 0.35),
                        color: crmColors.primary.main,
                        "&:hover": {
                          borderColor: crmColors.primary.main,
                          bgcolor: alpha(crmColors.primary.main, 0.06),
                        },
                      }}
                    >
                      Bill
                    </Button>
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>
      )}
    </PageContentLayout>
  );
};

export default ProductList;
